import { useContext } from "react";
import { ToolContext } from "../context/ToolContext";
import { FormGroup, Button, Col, Row } from "reactstrap";
import { Asset } from "../model/viewModel/asset";
import { Node } from "../model/viewModel/node";

interface CopyPasteFormPropertiesProps {
  onPaste: (properties: Asset | null | undefined) => void;
  assetToCopy: Asset;
}

const CopyPasteFormProperties = ({ onPaste, assetToCopy }: CopyPasteFormPropertiesProps) => {
  const { toolState, setToolState } = useContext(ToolContext);
  const { clickedAsset, propertiesClipboard } = toolState;

  const pasteIsValid = (() => {
    if (!propertiesClipboard || !clickedAsset) return false;

    // Styles are used here because styles contains class/type information
    const clipboardStyles = (propertiesClipboard?.asset as Asset)?.styles;
    const clickedStyles = (clickedAsset as Asset)?.styles;

    if (!clipboardStyles || !clickedStyles) return false;

    if (clickedStyles.class === "node") {
      const node = clickedAsset as Node;
      if (node.linkBox || node.potEnds || node.fuse) return false;
    }

    // Exception to allow consumers to be pasted onto nodes
    // Motors/Welders/Points of connection are not allowed to be pasted onto nodes
    if (
      clipboardStyles.type === "connectionPoint" &&
      clickedStyles.type === "groupedConnection" &&
      clickedStyles.class === "node" &&
      !["motor", "welder", "point-of-connection"].includes(clipboardStyles.class)
    ) {
      return true;
    }

    const consumerClasses = [
      "domestic",
      "small-commercial",
      "large-commercial",
      "industrial",
      "heatPump",
      "evChargePoint",
      "generator",
    ];

    const isClipboardGroupedConnection =
      clipboardStyles.type === "groupedConnection" && clipboardStyles.class === "connection";
    const isClickedGroupedConnection =
      clickedStyles.type === "groupedConnection" && clickedStyles.class === "connection";

    const isClipboardConsumer =
      consumerClasses.includes(clipboardStyles.class) || isClipboardGroupedConnection;
    const isClickedConsumer =
      consumerClasses.includes(clickedStyles.class) || isClickedGroupedConnection;

    if (isClipboardConsumer && isClickedConsumer) return true;

    return clipboardStyles.class === clickedStyles.class;
  })();

  const handleCopy = () => {
    setToolState({
      ...toolState,
      propertiesClipboard: { asset: { ...(assetToCopy as Asset) }, lastPastedToId: null },
    });
  };

  const handlePaste = () => {
    if (propertiesClipboard?.asset === null || propertiesClipboard?.asset === undefined) return;

    onPaste(propertiesClipboard?.asset);

    setToolState({
      ...toolState,
      propertiesClipboard: {
        ...propertiesClipboard,
        lastPastedToId: (clickedAsset as Asset)?.id || null,
      },
    });
  };

  return (
    <FormGroup>
      <Row>
        <Col sm={6}>
          <Button color="btn btn-primary btn-sm" className="w-100" onClick={handleCopy}>
            Copy Properties
          </Button>
        </Col>
        <Col sm={6}>
          <Button
            className="w-100"
            color="btn btn-primary btn-sm"
            disabled={!pasteIsValid}
            onClick={handlePaste}
          >
            Paste Properties
          </Button>
        </Col>
      </Row>
    </FormGroup>
  );
};

export default CopyPasteFormProperties;
